import { useState, useEffect, useRef } from 'react';
import { CollaborationProvider, createCollaborationProvider, ConnectionStatus, CollaborationUser } from './collaboration';
import { useAuthStore } from './store';

interface UseCollaborationOptions {
  documentId: string;
  enabled?: boolean;
}

interface UseCollaborationReturn {
  status: ConnectionStatus;
  users: CollaborationUser[];
  lastSaved: number | null;
  provider: CollaborationProvider | null;
}

/**
 * React hook for managing real-time collaboration
 */
export function useCollaboration({ documentId, enabled = true }: UseCollaborationOptions): UseCollaborationReturn {
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [users, setUsers] = useState<CollaborationUser[]>([]);
  const [lastSaved, setLastSaved] = useState<number | null>(null);
  const providerRef = useRef<CollaborationProvider | null>(null);
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    if (!enabled || !documentId) {
      setStatus('offline');
      return;
    }

    // Create provider
    const provider = createCollaborationProvider(documentId);
    providerRef.current = provider;

    // Subscribe to status changes
    const unsubStatus = provider.onStatusChange((newStatus) => {
      setStatus(newStatus);
    });

    // Subscribe to users changes
    const unsubUsers = provider.onUsersChange((newUsers) => {
      setUsers(newUsers);
    });

    // Subscribe to save events
    const unsubSave = provider.onSave((timestamp) => {
      setLastSaved(timestamp);
    });

    // Initial state
    setStatus(provider.getStatus());
    setUsers(provider.getUsers());

    return () => {
      unsubStatus();
      unsubUsers();
      unsubSave();
      provider.destroy();
      providerRef.current = null;
    };
  }, [documentId, enabled, user?.id, user?.name]);

  return {
    status,
    users,
    lastSaved,
    provider: providerRef.current,
  };
}

/**
 * Get status display info
 */
export function getStatusInfo(status: ConnectionStatus, lastSaved?: number | null): { label: string; color: string; icon: string } {
  switch (status) {
    case 'connecting':
      return { label: 'Connecting...', color: 'text-yellow-500', icon: '⏳' };
    case 'connected':
      return { label: 'Connected', color: 'text-green-500', icon: '🟢' };
    case 'disconnected':
      return { label: 'Reconnecting...', color: 'text-orange-500', icon: '🔄' };
    case 'offline':
      return { label: 'Offline', color: 'text-red-500', icon: '🔴' };
    default:
      return { label: 'Unknown', color: 'text-gray-500', icon: '❓' };
  }
}

function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}
